import React, { useState, useMemo } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiCheck,
  FiSearch,
  FiClock,
  FiBriefcase,
  FiTarget,
  FiX,
  FiEye,
} from "react-icons/fi";
import { isTaskOverdue } from "./TasksColumn";
import "../../assets/css/Tasks/TaskCalendarView.css";

const WEEKDAYS_FULL = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WEEKDAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAYS_MINI = ["M", "T", "W", "T", "F", "S", "S"];

const formatDateKey = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getTaskEffectiveStatus = (task) => {
  if (task.status === "Completed") return "Completed";
  if (isTaskOverdue(task)) return "Overdue";
  return "Pending";
};

export default function TaskCalendarView({
  tasks = [],
  onTaskClick,
  onCompleteTask,
  loading = false,
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState("month"); // 'month' | 'week' | 'day'
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedTaskForModal, setSelectedTaskForModal] = useState(null);
  const [dayExpandedDate, setDayExpandedDate] = useState(null);

  const todayStr = useMemo(() => formatDateKey(new Date()), []);

  // Filter tasks based on calendar toolbar search and status
  const filteredTasks = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return tasks.filter((t) => {
      const effStatus = getTaskEffectiveStatus(t);
      if (statusFilter !== "ALL" && effStatus !== statusFilter) return false;
      if (
        q &&
        !t.title?.toLowerCase().includes(q) &&
        !t.description?.toLowerCase().includes(q) &&
        !t.lead_title?.toLowerCase().includes(q) &&
        !t.deal_title?.toLowerCase().includes(q)
      ) {
        return false;
      }
      return true;
    });
  }, [tasks, searchQuery, statusFilter]);

  // Tasks mapped by date string YYYY-MM-DD
  const tasksByDate = useMemo(() => {
    const map = {};
    filteredTasks.forEach((t) => {
      if (!t.due_date) return;
      const key = formatDateKey(new Date(t.due_date));
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [filteredTasks]);

  // Navigation handlers
  const handlePrev = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      if (calendarView === "month") {
        d.setMonth(d.getMonth() - 1);
      } else if (calendarView === "week") {
        d.setDate(d.getDate() - 7);
      } else {
        d.setDate(d.getDate() - 1);
      }
      return d;
    });
  };

  const handleNext = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      if (calendarView === "month") {
        d.setMonth(d.getMonth() + 1);
      } else if (calendarView === "week") {
        d.setDate(d.getDate() + 7);
      } else {
        d.setDate(d.getDate() + 1);
      }
      return d;
    });
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Header display string
  const periodLabel = useMemo(() => {
    if (calendarView === "month") {
      return currentDate.toLocaleString("en-US", { month: "long", year: "numeric" });
    }
    if (calendarView === "week") {
      let dayIndex = currentDate.getDay() - 1;
      if (dayIndex < 0) dayIndex = 6;
      const mon = new Date(currentDate);
      mon.setDate(mon.getDate() - dayIndex);
      const sun = new Date(mon);
      sun.setDate(sun.getDate() + 6);

      const monM = mon.toLocaleString("en-US", { month: "short" });
      const sunM = sun.toLocaleString("en-US", { month: "short" });
      return `${monM} ${mon.getDate()} – ${sunM} ${sun.getDate()}, ${sun.getFullYear()}`;
    }
    return currentDate.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [currentDate, calendarView]);

  // Month grid calculation (Monday as start)
  const monthDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);

    let startDayOfWeek = firstOfMonth.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6;

    const startDate = new Date(firstOfMonth);
    startDate.setDate(startDate.getDate() - startDayOfWeek);

    const days = [];
    const cur = new Date(startDate);
    for (let i = 0; i < 35; i++) {
      days.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    if (days[34] < lastOfMonth) {
      for (let i = 0; i < 7; i++) {
        days.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
      }
    }
    return days;
  }, [currentDate]);

  // Week days calculation
  const weekDays = useMemo(() => {
    let dayIndex = currentDate.getDay() - 1;
    if (dayIndex < 0) dayIndex = 6;
    const mon = new Date(currentDate);
    mon.setDate(mon.getDate() - dayIndex);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(mon);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [currentDate]);

  const handleCardClick = (e, task) => {
    e.stopPropagation();
    setSelectedTaskForModal(task);
  };

  const handleShowMoreTasks = (e, dateKey) => {
    e.stopPropagation();
    setDayExpandedDate(dateKey);
  };

  return (
    <div className="task-calendar-clean-container">
      {/* Sleek Top Header Bar */}
      <div className="cal-header-bar">
        <div className="cal-header-nav-group">
          <button type="button" className="cal-btn-today" onClick={handleToday}>
            Today
          </button>
          <div className="cal-chevron-group">
            <button
              type="button"
              className="cal-btn-chevron"
              onClick={handlePrev}
              title="Previous period"
            >
              <FiChevronLeft size={16} />
            </button>
            <button
              type="button"
              className="cal-btn-chevron"
              onClick={handleNext}
              title="Next period"
            >
              <FiChevronRight size={16} />
            </button>
          </div>
          <h2 className="cal-period-heading">{periodLabel}</h2>
        </div>

        {/* Search & Filter Controls */}
        <div className="cal-header-filters">
          <div className="cal-search-pill">
            <FiSearch className="cal-search-ico" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="cal-clear-search-btn"
                onClick={() => setSearchQuery("")}
              >
                <FiX size={12} />
              </button>
            )}
          </div>

          <select
            className="cal-status-pill-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Completed">Completed</option>
            <option value="Overdue">Overdue</option>
          </select>

          {/* Segmented View Switcher */}
          <div className="cal-view-segmented-control">
            <button
              type="button"
              className={`cal-view-tab ${calendarView === "month" ? "active" : ""}`}
              onClick={() => setCalendarView("month")}
            >
              Month
            </button>
            <button
              type="button"
              className={`cal-view-tab ${calendarView === "week" ? "active" : ""}`}
              onClick={() => setCalendarView("week")}
            >
              Week
            </button>
            <button
              type="button"
              className={`cal-view-tab ${calendarView === "day" ? "active" : ""}`}
              onClick={() => setCalendarView("day")}
            >
              Day
            </button>
          </div>
        </div>
      </div>

      {/* Sleek Sub-bar: Task Counts & Legend */}
      <div className="cal-sub-bar">
        <div className="cal-count-tag">
          <strong>{filteredTasks.length}</strong> tasks scheduled
        </div>

        <div className="cal-legend-group">
          <span className="cal-legend-badge pending">
            <span className="cal-legend-indicator" /> Pending
          </span>
          <span className="cal-legend-badge completed">
            <span className="cal-legend-indicator" /> Completed (+5 pts)
          </span>
          <span className="cal-legend-badge overdue">
            <span className="cal-legend-indicator" /> Overdue (-5 pts)
          </span>
        </div>
      </div>

      {/* Main Grid View */}
      <div className="cal-view-viewport">
        {calendarView === "month" && (
          <div className="cal-month-board">
            {/* Weekday Titles */}
            <div className="cal-weekdays-row">
              {WEEKDAYS_SHORT.map((w, idx) => (
                <div key={w} className="cal-weekday-cell">
                  <span className="weekday-desktop">{w}</span>
                  <span className="weekday-mobile">{WEEKDAYS_MINI[idx]}</span>
                </div>
              ))}
            </div>

            {/* Month Day Cells */}
            <div className="cal-days-grid">
              {monthDays.map((d, index) => {
                const dateKey = formatDateKey(d);
                const isCurrentMonth = d.getMonth() === currentDate.getMonth();
                const isToday = dateKey === todayStr;
                const dayTasks = tasksByDate[dateKey] || [];
                const maxVisible = 2;
                const visibleTasks = dayTasks.slice(0, maxVisible);
                const hiddenCount = dayTasks.length - maxVisible;

                return (
                  <div
                    key={index}
                    className={`cal-cell ${
                      !isCurrentMonth ? "cal-cell-other-month" : ""
                    } ${isToday ? "cal-cell-today" : ""}`}
                    onClick={() => {
                      setCurrentDate(new Date(d));
                      setCalendarView("day");
                    }}
                  >
                    <div className="cal-cell-header">
                      <span className={`cal-cell-day-num ${isToday ? "today-badge" : ""}`}>
                        {d.getDate()}
                      </span>
                    </div>

                    <div className="cal-cell-tasks-stack">
                      {visibleTasks.map((t) => {
                        const status = getTaskEffectiveStatus(t);
                        const isDone = status === "Completed";
                        return (
                          <div
                            key={t.id}
                            className={`cal-card-chip status-${status.toLowerCase()}`}
                            onClick={(e) => handleCardClick(e, t)}
                            title={`${t.title} (${status})`}
                          >
                            <span className="cal-chip-accent" />
                            <span className="cal-chip-title-text">{t.title}</span>
                            <span
                              className={`cal-chip-score-tag ${
                                isDone ? "tag-plus" : status === "Overdue" ? "tag-minus" : "tag-neutral"
                              }`}
                            >
                              {isDone
                                ? `+${t.points ?? 5}`
                                : status === "Overdue"
                                ? "-5"
                                : "+5"}
                            </span>
                          </div>
                        );
                      })}

                      {hiddenCount > 0 && (
                        <button
                          type="button"
                          className="cal-btn-more-tasks"
                          onClick={(e) => handleShowMoreTasks(e, dateKey)}
                          title="View all tasks for this day"
                        >
                          +{hiddenCount} more
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {calendarView === "week" && (
          <div className="cal-week-board">
            <div className="cal-week-columns">
              {weekDays.map((d, idx) => {
                const dateKey = formatDateKey(d);
                const isToday = dateKey === todayStr;
                const dayTasks = tasksByDate[dateKey] || [];

                return (
                  <div key={idx} className="cal-week-column">
                    <div className={`cal-week-column-header ${isToday ? "header-today" : ""}`}>
                      <span className="week-day-title">{WEEKDAYS_SHORT[idx]}</span>
                      <span className={`week-day-num ${isToday ? "today-circle" : ""}`}>
                        {d.getDate()}
                      </span>
                    </div>

                    <div className="cal-week-column-content">
                      {dayTasks.length === 0 ? (
                        <div className="cal-empty-day-hint">No tasks</div>
                      ) : (
                        dayTasks.map((t) => {
                          const status = getTaskEffectiveStatus(t);
                          const isDone = status === "Completed";
                          return (
                            <div
                              key={t.id}
                              className={`cal-week-task-card status-${status.toLowerCase()}`}
                              onClick={(e) => handleCardClick(e, t)}
                            >
                              <div className="cal-week-card-top">
                                <span className="cal-card-status-dot" />
                                <span className="cal-week-card-title">{t.title}</span>
                              </div>
                              <div className="cal-week-card-footer">
                                <span className="cal-week-card-status">{status}</span>
                                <span
                                  className={`cal-week-pts-pill ${
                                    isDone
                                      ? "pts-green"
                                      : status === "Overdue"
                                      ? "pts-red"
                                      : "pts-neutral"
                                  }`}
                                >
                                  {isDone ? `+${t.points ?? 5} pts` : status === "Overdue" ? "-5 pts" : "5 pts"}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {calendarView === "day" && (
          <div className="cal-day-board">
            <div className="cal-day-board-header">
              <div>
                <h3>
                  {currentDate.toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </h3>
                <p>
                  {(tasksByDate[formatDateKey(currentDate)] || []).length} scheduled tasks for this date
                </p>
              </div>
            </div>

            <div className="cal-day-items-list">
              {(tasksByDate[formatDateKey(currentDate)] || []).length === 0 ? (
                <div className="cal-day-empty-placeholder">
                  <FiCalendar size={40} color="#94a3b8" />
                  <h4>No tasks scheduled for this day</h4>
                  <p>You have a clear schedule for this date.</p>
                </div>
              ) : (
                (tasksByDate[formatDateKey(currentDate)] || []).map((t) => {
                  const status = getTaskEffectiveStatus(t);
                  const isDone = status === "Completed";
                  return (
                    <div
                      key={t.id}
                      className={`cal-day-task-row status-${status.toLowerCase()}`}
                      onClick={(e) => handleCardClick(e, t)}
                    >
                      <div className="cal-day-task-row-main">
                        <span className="cal-row-status-dot" />
                        <div className="cal-row-content">
                          <h4 className="cal-row-title">{t.title}</h4>
                          {t.description && (
                            <p className="cal-row-desc">{t.description}</p>
                          )}
                          <div className="cal-row-tags">
                            {t.due_date && (
                              <span className="cal-row-tag time">
                                <FiClock size={11} />{" "}
                                {new Date(t.due_date).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            )}
                            {t.deal_title && (
                              <span className="cal-row-tag deal">
                                <FiBriefcase size={11} /> {t.deal_title}
                              </span>
                            )}
                            {t.lead_title && (
                              <span className="cal-row-tag lead">
                                <FiTarget size={11} /> {t.lead_title}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="cal-day-task-row-actions">
                        <span className={`cal-status-pill-badge ${status.toLowerCase()}`}>
                          {status}
                        </span>
                        <span
                          className={`cal-points-pill-badge ${
                            isDone ? "earned" : status === "Overdue" ? "penalty" : "pending"
                          }`}
                        >
                          {isDone ? `+${t.points ?? 5} pts` : status === "Overdue" ? "-5 pts" : "+5 pts"}
                        </span>
                        {!isDone && onCompleteTask && (
                          <button
                            type="button"
                            className="cal-btn-action-complete"
                            onClick={(e) => {
                              e.stopPropagation();
                              onCompleteTask(t);
                            }}
                          >
                            <FiCheck size={13} /> Complete & Update Follow-up
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Task Details Modal */}
      {selectedTaskForModal && (
        <div
          className="cal-modal-backdrop-clean"
          onClick={() => setSelectedTaskForModal(null)}
        >
          <div
            className="cal-modal-card-clean"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cal-modal-card-header">
              <h3>{selectedTaskForModal.title}</h3>
              <button
                type="button"
                className="cal-modal-btn-close"
                onClick={() => setSelectedTaskForModal(null)}
              >
                <FiX />
              </button>
            </div>

            <div className="cal-modal-card-body">
              <div className="cal-modal-row">
                <span className="cal-modal-label">Status:</span>
                <span
                  className={`cal-status-pill-badge ${getTaskEffectiveStatus(
                    selectedTaskForModal
                  ).toLowerCase()}`}
                >
                  {getTaskEffectiveStatus(selectedTaskForModal)}
                </span>
              </div>

              <div className="cal-modal-row">
                <span className="cal-modal-label">Points Impact:</span>
                <span
                  className={`cal-points-pill-badge ${
                    selectedTaskForModal.status === "Completed"
                      ? "earned"
                      : isTaskOverdue(selectedTaskForModal)
                      ? "penalty"
                      : "pending"
                  }`}
                >
                  {selectedTaskForModal.status === "Completed"
                    ? `+${selectedTaskForModal.points ?? 5} pts (Awarded)`
                    : isTaskOverdue(selectedTaskForModal)
                    ? "-5 pts (Overdue Completion Penalty)"
                    : "+5 pts (Upon On-Time Completion)"}
                </span>
              </div>

              <div className="cal-modal-row">
                <span className="cal-modal-label">Due Date:</span>
                <span className="cal-modal-val">
                  {selectedTaskForModal.due_date
                    ? new Date(selectedTaskForModal.due_date).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "No Due Date"}
                </span>
              </div>

              {selectedTaskForModal.assigned_user_name && (
                <div className="cal-modal-row">
                  <span className="cal-modal-label">Assigned To:</span>
                  <span className="cal-modal-val">
                    {selectedTaskForModal.assigned_user_name}
                  </span>
                </div>
              )}

              {(selectedTaskForModal.deal_title || selectedTaskForModal.lead_title) && (
                <div className="cal-modal-row">
                  <span className="cal-modal-label">Related To:</span>
                  <span className="cal-modal-val">
                    {selectedTaskForModal.deal_title
                      ? `Deal: ${selectedTaskForModal.deal_title}`
                      : `Lead: ${selectedTaskForModal.lead_title}`}
                  </span>
                </div>
              )}

              {selectedTaskForModal.description && (
                <div className="cal-modal-desc-container">
                  <span className="cal-modal-label">Description:</span>
                  <p>{selectedTaskForModal.description}</p>
                </div>
              )}
            </div>

            <div className="cal-modal-card-footer">
              <button
                type="button"
                className="cal-btn-view-link"
                onClick={() => {
                  const t = selectedTaskForModal;
                  setSelectedTaskForModal(null);
                  if (onTaskClick) onTaskClick(t.id);
                }}
              >
                <FiEye /> View Lead / Deal
              </button>

              {selectedTaskForModal.status !== "Completed" && onCompleteTask && (
                <button
                  type="button"
                  className="cal-btn-submit-complete"
                  onClick={() => {
                    const t = selectedTaskForModal;
                    setSelectedTaskForModal(null);
                    onCompleteTask(t);
                  }}
                >
                  <FiCheck /> Complete & Update Follow-up
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Day Expanded Tasks Modal (When clicking +N more) */}
      {dayExpandedDate && (
        <div
          className="cal-modal-backdrop-clean"
          onClick={() => setDayExpandedDate(null)}
        >
          <div
            className="cal-modal-card-clean"
            style={{ maxWidth: "560px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cal-modal-card-header">
              <h3>
                Tasks for{" "}
                {new Date(dayExpandedDate + "T12:00:00").toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </h3>
              <button
                type="button"
                className="cal-modal-btn-close"
                onClick={() => setDayExpandedDate(null)}
              >
                <FiX />
              </button>
            </div>

            <div className="cal-modal-card-body" style={{ maxHeight: "380px", overflowY: "auto" }}>
              {(tasksByDate[dayExpandedDate] || []).map((t) => {
                const status = getTaskEffectiveStatus(t);
                const isDone = status === "Completed";
                return (
                  <div
                    key={t.id}
                    className={`cal-expanded-task-item status-${status.toLowerCase()}`}
                    onClick={() => {
                      setDayExpandedDate(null);
                      setSelectedTaskForModal(t);
                    }}
                  >
                    <div>
                      <div className="cal-expanded-task-title">{t.title}</div>
                      <div className="cal-expanded-task-sub">
                        {status} • {t.assigned_user_name || "Unassigned"}
                      </div>
                    </div>
                    <span
                      className={`cal-points-pill-badge ${
                        isDone ? "earned" : status === "Overdue" ? "penalty" : "pending"
                      }`}
                    >
                      {isDone ? `+${t.points ?? 5} pts` : status === "Overdue" ? "-5 pts" : "+5 pts"}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="cal-modal-card-footer">
              <button
                type="button"
                className="cal-btn-view-link"
                onClick={() => setDayExpandedDate(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
